/**
 * Nested Cart Items Handler
 * Transforms nested packaging items into collapsible dropdowns
 */

class NestedCartItemsHandler {
  constructor() {
    this.init();
  }

  init() {
    // Transform cart on load
    this.transformCartItems();

    // Watch for cart updates
    this.observeCartChanges();

    // Listen for Shopify cart update events
    document.addEventListener('cart:updated', () => {
      setTimeout(() => this.transformCartItems(), 100);
    });

    if (window.Shopify && window.Shopify.PubSub) {
      window.Shopify.PubSub.subscribe('cart:updated', () => {
        setTimeout(() => this.transformCartItems(), 100);
      });
    }
  }

  /**
   * Find and transform nested items into collapsible dropdowns
   */
  transformCartItems() {
    const cartContainers = [
      document.querySelector('#main-cart-items'),
      document.querySelector('#CartDrawer-CartItems'),
      document.querySelector('cart-items')
    ].filter(Boolean);

    cartContainers.forEach(container => {
      this.processCartContainer(container);
    });
  }

  /**
   * Process a single cart container
   */
  async processCartContainer(container) {
    // Fetch current cart data to identify parent-child relationships
    const cartData = await this.fetchCartData();
    if (!cartData) return;

    // Build map of addon items by parent key
    const addonsByParent = new Map();
    
    cartData.items.forEach(item => {
      if (item.properties && item.properties._parent_key) {
        const parentKey = item.properties._parent_key;
        if (!addonsByParent.has(parentKey)) {
          addonsByParent.set(parentKey, []);
        }
        addonsByParent.set(parentKey, [...addonsByParent.get(parentKey), item]);
      }
    });

    // Process each cart item
    const cartItems = container.querySelectorAll('.cart-item');

    cartItems.forEach(mainItemElement => {
      // Get the item key from the element
      const itemKey = this.getItemKeyFromElement(mainItemElement);
      
      if (!itemKey) return;

      // Check if this item has addons
      const addons = addonsByParent.get(itemKey);
      
      if (addons && addons.length > 0) {
        // Check if addon wrapper already exists
        let addonsWrapper = mainItemElement.querySelector('.cart-item__addons-wrapper');
        
        if (!addonsWrapper) {
          addonsWrapper = this.createAddonsDropdownFromData(mainItemElement, addons);
          
          // Insert after cart item details
          const detailsCell = mainItemElement.querySelector('.cart-item__details');
          if (detailsCell) {
            detailsCell.appendChild(addonsWrapper);
          }
        }
      }
    });

    // Hide addon items from the cart display
    this.hideAddonItems(container, cartData);
  }

  /**
   * Fetch cart data via AJAX
   */
  async fetchCartData() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart:', error);
      return null;
    }
  }

  /**
   * Get item key from cart item element
   */
  getItemKeyFromElement(element) {
    // Try to find the key in various places
    const keyAttr = element.getAttribute('data-key') || 
                    element.getAttribute('data-line-item-key') ||
                    element.getAttribute('data-cart-item-key');
    
    if (keyAttr) return keyAttr;

    // Try to extract from quantity input name attribute
    const quantityInput = element.querySelector('[name^="updates"]');
    if (quantityInput) {
      const match = quantityInput.name.match(/updates\[(.*?)\]/);
      if (match) return match[1];
    }

    // Try to get from cart item URL
    const removeButton = element.querySelector('[href*="/cart/change"]');
    if (removeButton) {
      const urlMatch = removeButton.href.match(/line=(\d+)/);
      if (urlMatch) return urlMatch[1];
    }

    return null;
  }

  /**
   * Hide addon items from main cart display
   */
  hideAddonItems(container, cartData) {
    cartData.items.forEach((item, index) => {
      if (item.properties && item.properties._is_addon === 'true') {
        // Find the cart item element for this addon and hide it
        const cartItems = container.querySelectorAll('.cart-item');
        cartItems.forEach(el => {
          const key = this.getItemKeyFromElement(el);
          if (key === item.key || key === String(index + 1)) {
            el.style.display = 'none';
            el.classList.add('cart-item--addon');
          }
        });
      }
    });
  }

  /**
   * Create collapsible addons dropdown from cart data
   */
  createAddonsDropdownFromData(mainItem, addons) {
    const wrapper = document.createElement('div');
    wrapper.className = 'cart-item__addons-wrapper';

    // Create toggle button
    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'cart-item__addons-toggle';
    toggle.setAttribute('aria-expanded', 'false');
    
    const addonCount = addons.length;
    const addonLabel = addonCount === 1 ? 'אפשרות נוספת' : `${addonCount} אפשרויות נוספות`;
    
    toggle.innerHTML = `
      <span>
        <span class="cart-item__addon-badge">+${addonCount}</span>
        ${addonLabel}
      </span>
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    `;

    // Create content container
    const content = document.createElement('div');
    content.className = 'cart-item__addons-content';

    // Add each addon to dropdown
    addons.forEach(addon => {
      const addonElement = this.createAddonElementFromData(addon);
      content.appendChild(addonElement);
    });

    // Toggle functionality
    toggle.addEventListener('click', () => {
      const isOpen = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', !isOpen);
      content.classList.toggle('is-open', !isOpen);
    });

    wrapper.appendChild(toggle);
    wrapper.appendChild(content);

    return wrapper;
  }

  /**
   * Create addon element from cart item data
   */
  createAddonElementFromData(item) {
    const addon = document.createElement('div');
    addon.className = 'cart-item__addon-item';

    // Format price
    const price = this.formatMoney(item.final_line_price);
    const title = item.properties && item.properties._addon_label ? 
                  item.properties._addon_label : 
                  item.product_title;

    // Build addon HTML
    addon.innerHTML = `
      ${item.image ? `<img src="${item.image}" alt="${title}" class="cart-item__addon-image" loading="lazy">` : ''}
      <div class="cart-item__addon-details">
        <div class="cart-item__addon-name">${title}</div>
        ${item.quantity > 1 ? `<div class="cart-item__addon-quantity">כמות: ${item.quantity}</div>` : ''}
      </div>
      <div class="cart-item__addon-price">${price}</div>
    `;

    return addon;
  }

  /**
   * Format money using Shopify's formatter
   */
  formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    return '₪' + (cents / 100).toFixed(2);
  }

  /**
   * Observe DOM changes to cart
   */
  observeCartChanges() {
    const observer = new MutationObserver((mutations) => {
      // Check if cart items were added/removed
      const cartChanged = mutations.some(mutation => 
        mutation.type === 'childList' && 
        (mutation.target.classList?.contains('cart-items') || 
         mutation.target.id === 'main-cart-items' ||
         mutation.target.id === 'CartDrawer-CartItems')
      );

      if (cartChanged) {
        setTimeout(() => this.transformCartItems(), 50);
      }
    });

    // Observe cart containers
    const cartContainers = [
      document.querySelector('#main-cart-items'),
      document.querySelector('#CartDrawer-CartItems'),
      document.querySelector('cart-items tbody')
    ].filter(Boolean);

    cartContainers.forEach(container => {
      observer.observe(container, {
        childList: true,
        subtree: true
      });
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.nestedCartHandler = new NestedCartItemsHandler();
  });
} else {
  window.nestedCartHandler = new NestedCartItemsHandler();
}
